import { z } from "zod";
import { fcfa, useApi } from "../../api/hooks";
import { EtatChargement, EtatErreur } from "../../composants/Avis";
import { Tableau, Tuiles } from "../../composants/Tableau";
import type { EcranProps } from "../types";

// ===========================================================================
// JOURNAL D'INTERVENTION — la preuve de diligence de l'agence
// ===========================================================================

const InterventionsSchema = z.object({
  interventions: z.array(
    z.object({
      id: z.string(),
      date: z.string(),
      type: z.string(),
      objet: z.string(),
      dureeHeures: z.number(),
      lieu: z.string().nullable(),
      livrable: z.string().nullable(),
      statut: z.string().nullable(),
      suiteADonner: z.string().nullable(),
      intervenant: z.string().nullable(),
    }),
  ),
  synthese: z.object({
    nombre: z.number(),
    totalHeures: z.number(),
    parType: z.array(z.object({ type: z.string(), nombre: z.number(), heures: z.number() })),
    sansSuite: z.number(),
  }),
});

export function Interventions({ dossierId, annee }: EcranProps) {
  const q = useApi(
    ["interventions", dossierId, annee],
    `/dossiers/${dossierId}/relation/interventions?annee=${annee}`,
    InterventionsSchema,
  );

  if (q.isLoading) return <EtatChargement quoi="du journal d'intervention" />;
  if (q.error) return <EtatErreur erreur={q.error} />;
  if (!q.data) return null;

  return (
    <div>
      <p className="resume">
        Le journal d'intervention est <strong>la preuve de diligence de l'agence</strong>.
      </p>

      <Tuiles
        tuiles={[
          { titre: "Interventions", valeur: q.data.synthese.nombre },
          { titre: "Heures", valeur: q.data.synthese.totalHeures.toFixed(1) },
          {
            titre: "Avec suite à donner",
            valeur: q.data.synthese.sansSuite,
            alerte: q.data.synthese.sansSuite > 0,
          },
        ]}
      />

      <section>
        <h2>Interventions {annee}</h2>
        <Tableau
          colonnes={[
            { cle: "d", titre: "Date", rendu: (i) => i.date },
            { cle: "t", titre: "Type", rendu: (i) => i.type },
            { cle: "o", titre: "Objet", rendu: (i) => i.objet },
            { cle: "h", titre: "Durée", montant: true, rendu: (i) => `${i.dureeHeures.toFixed(1)} h` },
            { cle: "l", titre: "Lieu", rendu: (i) => i.lieu ?? "—" },
            { cle: "liv", titre: "Livrable", rendu: (i) => i.livrable ?? "—" },
            { cle: "s", titre: "Statut", rendu: (i) => i.statut ?? "—" },
            { cle: "su", titre: "Suite à donner", rendu: (i) => i.suiteADonner ?? "—" },
            { cle: "int", titre: "Intervenant", rendu: (i) => i.intervenant ?? "—" },
          ]}
          lignes={q.data.interventions}
          cle={(i) => i.id}
          vide="Aucune intervention enregistrée sur l'exercice."
        />
      </section>

      <section>
        <h2>Répartition par type</h2>
        <Tableau
          colonnes={[
            { cle: "t", titre: "Type", rendu: (t) => t.type },
            { cle: "n", titre: "Nombre", montant: true, rendu: (t) => t.nombre },
            { cle: "h", titre: "Heures", montant: true, rendu: (t) => t.heures.toFixed(1) },
          ]}
          lignes={q.data.synthese.parType}
          cle={(t) => t.type}
        />
      </section>
    </div>
  );
}

// ===========================================================================
// REQUÊTES CLIENT
// ===========================================================================

const RequetesSchema = z.object({
  requetes: z.array(
    z.object({
      id: z.string(),
      dateReception: z.string(),
      dateTraitement: z.string().nullable(),
      canal: z.string(),
      objet: z.string(),
      description: z.string().nullable(),
      traitePar: z.string().nullable(),
      statut: z.string().nullable(),
      noteSatisfaction: z.number().nullable(),
      delaiJours: z.number().nullable(),
    }),
  ),
  synthese: z.object({
    nombre: z.number(),
    enCours: z.number(),
    delaiMoyenJours: z.number().nullable(),
    satisfactionMoyenne: z.number().nullable(),
    parCanal: z.array(z.object({ canal: z.string(), nombre: z.number() })),
  }),
});

export function Requetes({ dossierId }: EcranProps) {
  const q = useApi(
    ["requetes", dossierId],
    `/dossiers/${dossierId}/relation/requetes`,
    RequetesSchema,
  );

  if (q.isLoading) return <EtatChargement quoi="des requêtes client" />;
  if (q.error) return <EtatErreur erreur={q.error} />;
  if (!q.data) return null;

  const s = q.data.synthese;

  return (
    <div>
      <Tuiles
        tuiles={[
          { titre: "Requêtes", valeur: s.nombre },
          { titre: "En cours", valeur: s.enCours, alerte: s.enCours > 0 },
          {
            titre: "Délai moyen",
            valeur: s.delaiMoyenJours === null ? "—" : `${s.delaiMoyenJours.toFixed(1)} j`,
          },
          {
            titre: "Satisfaction",
            valeur: s.satisfactionMoyenne === null ? "—" : `${s.satisfactionMoyenne.toFixed(1)} / 5`,
          },
        ]}
      />

      <section>
        <h2>Requêtes</h2>
        <Tableau
          colonnes={[
            { cle: "r", titre: "Reçue le", rendu: (r) => r.dateReception },
            { cle: "c", titre: "Canal", rendu: (r) => r.canal },
            { cle: "o", titre: "Objet", rendu: (r) => r.objet },
            { cle: "t", titre: "Traitée le", rendu: (r) => r.dateTraitement ?? "—" },
            {
              cle: "d",
              titre: "Délai",
              montant: true,
              // Le délai est CALCULÉ, jamais saisi.
              rendu: (r) => (r.delaiJours === null ? "—" : `${r.delaiJours} j`),
            },
            { cle: "p", titre: "Traitée par", rendu: (r) => r.traitePar ?? "—" },
            { cle: "s", titre: "Statut", rendu: (r) => r.statut ?? "—" },
            {
              cle: "n",
              titre: "Satisfaction",
              montant: true,
              rendu: (r) => (r.noteSatisfaction === null ? "—" : `${r.noteSatisfaction} / 5`),
            },
          ]}
          lignes={q.data.requetes}
          cle={(r) => r.id}
          vide="Aucune requête enregistrée."
        />
      </section>
    </div>
  );
}

// ===========================================================================
// SUIVI EN DIRECT — GÉRANT SEUL
// ===========================================================================

const SuiviSchema = z.array(
  z.object({
    id: z.string(),
    date: z.string(),
    numeroPiece: z.string().nullable(),
    libelle: z.string(),
    nature: z.string(),
    tiers: z.string().nullable(),
    sens: z.string(),
    montant: z.number(),
    validePar: z.string().nullable(),
    montantInvraisemblable: z.boolean(),
    nouveau: z.boolean(),
    notes: z.array(
      z.object({
        id: z.string(),
        type: z.string(),
        texte: z.string(),
        auteur: z.string(),
        creeeLe: z.string(),
      }),
    ),
  }),
);

export function SuiviDirect({ dossierId, estGerant }: EcranProps) {
  // Auto-actualisation toutes les 20 s, UNIQUEMENT quand l'écran est visible.
  const q = useApi(["suivi-direct", dossierId], `/dossiers/${dossierId}/journal/suivi-direct`, SuiviSchema, {
    actif: estGerant,
  });

  if (!estGerant)
    return <p className="alerte">Cet écran est réservé au gérant.</p>;

  if (q.isLoading) return <EtatChargement quoi="du suivi en direct" />;
  if (q.error) return <EtatErreur erreur={q.error} />;

  return (
    <div>
      <p className="resume">
        Les 60 dernières écritures du client. Cet écran et les notes internes{" "}
        <strong>ne s'affichent nulle part ailleurs</strong> — aucun canal vers le
        portail client.
      </p>

      <Tableau
        colonnes={[
          { cle: "d", titre: "Date", rendu: (l) => l.date },
          { cle: "p", titre: "Pièce", rendu: (l) => l.numeroPiece ?? "—" },
          { cle: "l", titre: "Libellé", rendu: (l) => l.libelle },
          { cle: "n", titre: "Nature", rendu: (l) => l.nature },
          { cle: "t", titre: "Tiers", rendu: (l) => l.tiers ?? "—" },
          { cle: "s", titre: "Sens", rendu: (l) => l.sens },
          {
            cle: "m",
            titre: "Montant",
            montant: true,
            rendu: (l) => (
              <span className={l.montantInvraisemblable ? "montant-suspect" : undefined}>
                {fcfa(l.montant)}
              </span>
            ),
          },
          { cle: "v", titre: "Validé par", rendu: (l) => l.validePar ?? "—" },
          {
            cle: "note",
            titre: "Notes internes",
            rendu: (l) =>
              l.notes.length === 0 ? "—" : l.notes.map((n) => `${n.type} : ${n.texte}`).join(" · "),
          },
        ]}
        lignes={q.data ?? []}
        cle={(l) => l.id}
        total={(l) => l.nouveau}
        vide="Aucune écriture."
      />
    </div>
  );
}
